import type {
  HostingServerDTO,
  HostingServerInput,
  ProjectHostingServerDTO,
  UpdateHostingServerInput,
  ApiTokenDTO,
  TaskAttachmentDTO,
  ApplicationDTO,
  CreateApiTokenInput,
  BrainGraphDTO,
  BrainNoteDTO,
  ProjectBrainDTO,
  CreateWorkerInput,
  ModelAnalyticsRowDTO,
  UpdateWorkerInput,
  WorkSessionDetailDTO,
  WorkSessionSummaryDTO,
  WorkerBlockerDTO,
  WorkerBlockerResolveInput,
  WorkerWorkaroundDTO,
  WorkerDTO,
  WorkersActivityDTO,
  CreateDecisionInput,
  CreateInitiativeInput,
  CompleteHumanTaskInput,
  CreateHumanTaskInput,
  CreateProposalInput,
  DecideProposalInput,
  DismissHumanTaskInput,
  CreateTaskInput,
  DecisionDTO,
  DepartmentDTO,
  FactoryPlanDTO,
  GoalDTO,
  InboxItemDTO,
  MilestoneDTO,
  MoveTaskInput,
  QaVerdictInput,
  HumanTaskDTO,
  ProposalDTO,
  ReclassifyDecisionInput,
  ReleaseDTO,
  ResolveDecisionInput,
  TaskDTO,
  TaskDependencyInput,
  TaskDetailDTO,
  UpdateTaskInput,
  UpdateWorkflowInput,
  UpsertDepartmentInput,
  UpsertGoalInput,
  UpsertMilestoneInput,
  UpsertReleaseInput,
  WorkflowDTO,
  AuditEventDTO,
  ChangeKnowledgeDocStatusInput,
  CodebaseDTO,
  CodebaseDependencyDTO,
  CreateApplicationInput,
  CreateCodebaseInput,
  CreateEnvironmentInput,
  CreateKnowledgeConflictInput,
  CreateKnowledgeDocInput,
  CreateProjectInput,
  CreateUserInput,
  DesignVersionDTO,
  EnvironmentDTO,
  EnvironmentDeploymentsDTO,
  KnowledgeConflictDTO,
  KnowledgeDocDTO,
  KnowledgeTreeDTO,
  OnboardProjectInput,
  ProjectDTO,
  ProjectDetailDTO,
  ProjectPolicyDTO,
  ProjectTemplateDTO,
  ResolveKnowledgeConflictInput,
  AutomationStateDTO,
  EconomyDTO,
  SessionInfoDTO,
  ThemePreference,
  SetCodebaseDependencyInput,
  SetProjectMemberInput,
  UpdateCodebaseInput,
  UpdateEnvironmentInput,
  UpdateKnowledgeDocInput,
  UpdateProjectInput,
  UpdateProjectPoliciesInput,
  UpdateUserInput,
  UploadDTO,
  UserDTO,
  RoutineDTO,
  RoutineRunDTO,
  CreateRoutineInput,
  UpdateRoutineInput,
  ConnectionDTO,
  CreateConnectionInput,
  UpdateConnectionInput,
  IncidentDTO,
  CreateIncidentInput,
  UpdateIncidentInput,
  ResolveIncidentInput,
  JarvisChatInput,
  JarvisConfirmInput,
  JarvisReplyDTO,
  JarvisStatusDTO,
  ProjectFeedbackDTO,
  ReviewScreenInput,
  UiScreenDTO,
  UiScreensSummaryDTO,
  UiScreenshotDTO,
} from "@app-factory/shared";

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      // CSRF ochrana — server vyžaduje custom header u mutací
      ...(method !== "GET" ? { "x-af-csrf": "1" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let code = "UNKNOWN";
    let message = `Chyba ${res.status}`;
    let details: unknown;
    try {
      const data = (await res.json()) as { error?: { code: string; message: string; details?: unknown } };
      if (data.error) {
        code = data.error.code;
        message = data.error.message;
        details = data.error.details;
      }
    } catch {
      // ponecháme výchozí
    }
    throw new ApiRequestError(res.status, code, message, details);
  }
  return res.json() as Promise<T>;
}

/** Multipart upload (soubor + volitelná pole) se stejnou CSRF ochranou. */
async function uploadRequest<T>(
  url: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const form = new FormData();
  // pole před souborem — server je čte z file.fields
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  form.append("file", file);
  const res = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-af-csrf": "1" },
    body: form,
  });
  if (!res.ok) {
    let message = `Chyba ${res.status}`;
    let code = "UNKNOWN";
    try {
      const data = (await res.json()) as { error?: { code: string; message: string } };
      if (data.error) {
        code = data.error.code;
        message = data.error.message;
      }
    } catch {
      // ponecháme výchozí
    }
    throw new ApiRequestError(res.status, code, message);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // auth
  login: (input: { email: string; password: string }) =>
    request<SessionInfoDTO>("POST", "/api/auth/login", input),
  logout: () => request<{ ok: true }>("POST", "/api/auth/logout"),
  me: () => request<SessionInfoDTO>("GET", "/api/auth/me"),

  // health
  health: () =>
    request<{ status: string; time: string; db: boolean; version: string; commit: string | null }>(
      "GET",
      "/api/health",
    ),

  // projects
  projects: () => request<{ projects: ProjectDTO[] }>("GET", "/api/projects"),
  project: (idOrSlug: string) =>
    request<{ project: ProjectDetailDTO }>("GET", `/api/projects/${encodeURIComponent(idOrSlug)}`),
  createProject: (input: CreateProjectInput) =>
    request<{ project: ProjectDTO }>("POST", "/api/projects", input),
  updateProject: (idOrSlug: string, input: UpdateProjectInput) =>
    request<{ project: ProjectDTO }>("PATCH", `/api/projects/${encodeURIComponent(idOrSlug)}`, input),
  setProjectMember: (idOrSlug: string, input: SetProjectMemberInput) =>
    request<{ ok: true }>("PUT", `/api/projects/${encodeURIComponent(idOrSlug)}/members`, input),
  removeProjectMember: (idOrSlug: string, userId: string) =>
    request<{ ok: true }>(
      "DELETE",
      `/api/projects/${encodeURIComponent(idOrSlug)}/members/${encodeURIComponent(userId)}`,
    ),
  deleteProject: (idOrSlug: string) =>
    request<{ ok: true }>("DELETE", `/api/projects/${encodeURIComponent(idOrSlug)}`),

  // Factory brain — poučení z provozu
  projectBrain: (projectId: string) =>
    request<ProjectBrainDTO>("GET", `/api/projects/${encodeURIComponent(projectId)}/brain`),
  brainNotes: (params: { scope?: string; projectId?: string; needsReview?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.scope) q.set("scope", params.scope);
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.needsReview) q.set("needsReview", "1");
    return request<{ notes: BrainNoteDTO[] }>("GET", `/api/brain/notes?${q.toString()}`);
  },
  updateBrainNote: (
    slug: string,
    input: {
      title?: string;
      readWhen?: string;
      body?: string;
      scope?: "FACTORY" | "PROJECT";
      archived?: boolean;
      reviewed?: boolean;
    },
  ) => request<{ note: BrainNoteDTO }>("PATCH", `/api/brain/notes/${encodeURIComponent(slug)}`, input),
  brainGraph: (params: { scope?: string; projectId?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.scope) q.set("scope", params.scope);
    if (params.projectId) q.set("projectId", params.projectId);
    return request<BrainGraphDTO>("GET", `/api/brain/graph?${q.toString()}`);
  },

  // audit
  audit: (params: { actorType?: string; projectId?: string; cursor?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params.actorType) q.set("actorType", params.actorType);
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.cursor) q.set("cursor", params.cursor);
    if (params.limit) q.set("limit", String(params.limit));
    return request<{ events: AuditEventDTO[]; nextCursor: string | null }>(
      "GET",
      `/api/audit?${q.toString()}`,
    );
  },

  // users
  users: () => request<{ users: UserDTO[] }>("GET", "/api/users"),
  createUser: (input: CreateUserInput) => request<{ user: UserDTO }>("POST", "/api/users", input),
  updateUser: (id: string, input: UpdateUserInput) =>
    request<{ user: UserDTO }>("PATCH", `/api/users/${encodeURIComponent(id)}`, input),

  // settings
  settings: () => request<{ settings: Record<string, string> }>("GET", "/api/settings"),
  updateSettings: (settings: Record<string, string>) =>
    request<{ ok: true }>("PUT", "/api/settings", { settings }),

  // OAuth souhlas s připojením MCP konektoru
  oauthConsent: (requestId: string) =>
    request<{
      clientName: string;
      redirectHost: string;
      scope: string;
      user: { name: string; email: string; role: string };
    }>("GET", `/api/oauth/authorize/${encodeURIComponent(requestId)}`),
  oauthConsentDecide: (requestId: string, approve: boolean) =>
    request<{ redirectTo: string }>(
      "POST",
      `/api/oauth/authorize/${encodeURIComponent(requestId)}/${approve ? "approve" : "deny"}`,
    ),

  // osobní API tokeny (MCP konektor) — `secret` se vrací jen při vydání
  apiTokens: () => request<{ tokens: ApiTokenDTO[] }>("GET", "/api/me/tokens"),
  createApiToken: (input: CreateApiTokenInput) =>
    request<{ token: ApiTokenDTO; secret: string }>("POST", "/api/me/tokens", input),
  revokeApiToken: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/me/tokens/${encodeURIComponent(id)}`),

  // ---------- Fáze 2 ----------

  // onboarding & šablony
  templates: () => request<{ templates: ProjectTemplateDTO[] }>("GET", "/api/templates"),
  onboardProject: (input: OnboardProjectInput) =>
    request<{ project: ProjectDTO }>("POST", "/api/projects/onboard", input),

  // applications & design
  applications: (projectId: string) =>
    request<{ applications: ApplicationDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/applications`,
    ),
  createApplication: (projectId: string, input: CreateApplicationInput) =>
    request<{ application: ApplicationDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/applications`,
      input,
    ),
  deleteApplication: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/applications/${encodeURIComponent(id)}`),
  uploadDesign: (applicationId: string, file: File) =>
    uploadRequest<{ designVersion: DesignVersionDTO }>(
      `/api/applications/${encodeURIComponent(applicationId)}/design`,
      file,
    ),
  setDesignCurrent: (applicationId: string, versionId: string) =>
    request<{ ok: true }>(
      "POST",
      `/api/applications/${encodeURIComponent(applicationId)}/design/${encodeURIComponent(versionId)}/current`,
    ),

  // uploads
  uploads: (projectId: string) =>
    request<{ uploads: UploadDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/uploads`,
    ),
  uploadFile: (projectId: string, file: File, note?: string) =>
    uploadRequest<{ upload: UploadDTO }>(
      `/api/projects/${encodeURIComponent(projectId)}/uploads`,
      file,
      note ? { note } : {},
    ),
  downloadUploadUrl: (id: string) => `/api/uploads/${encodeURIComponent(id)}/download`,
  reprocessUpload: (id: string) =>
    request<{ upload: UploadDTO }>("POST", `/api/uploads/${encodeURIComponent(id)}/reprocess`),

  // knowledge
  knowledgeTree: (projectId: string) =>
    request<KnowledgeTreeDTO>("GET", `/api/projects/${encodeURIComponent(projectId)}/knowledge`),
  knowledgeDoc: (id: string) =>
    request<{ doc: KnowledgeDocDTO }>("GET", `/api/knowledge/${encodeURIComponent(id)}`),
  createKnowledgeDoc: (projectId: string, input: CreateKnowledgeDocInput) =>
    request<{ doc: KnowledgeDocDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/knowledge`,
      input,
    ),
  updateKnowledgeDoc: (id: string, input: UpdateKnowledgeDocInput) =>
    request<{ ok: true }>("PUT", `/api/knowledge/${encodeURIComponent(id)}`, input),
  /** Samotný spouštěč — bez revize, protože se nemění pravda, jen nabízení. */
  setKnowledgeReadWhen: (id: string, readWhen: string) =>
    request<{ ok: true }>("PUT", `/api/knowledge/${encodeURIComponent(id)}/read-when`, { readWhen }),
  changeKnowledgeStatus: (id: string, input: ChangeKnowledgeDocStatusInput) =>
    request<{ ok: true }>("POST", `/api/knowledge/${encodeURIComponent(id)}/status`, input),
  knowledgeExportUrl: (projectId: string) =>
    `/api/projects/${encodeURIComponent(projectId)}/knowledge/export`,

  // konflikty
  conflicts: (projectId: string) =>
    request<{ conflicts: KnowledgeConflictDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/conflicts`,
    ),
  createConflict: (projectId: string, input: CreateKnowledgeConflictInput) =>
    request<{ conflict: KnowledgeConflictDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/conflicts`,
      input,
    ),
  resolveConflict: (id: string, input: ResolveKnowledgeConflictInput) =>
    request<{ ok: true }>("POST", `/api/conflicts/${encodeURIComponent(id)}/resolve`, input),

  // codebases
  codebases: (projectId: string) =>
    request<{ codebases: CodebaseDTO[]; dependencies: CodebaseDependencyDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/codebases`,
    ),
  createCodebase: (projectId: string, input: CreateCodebaseInput) =>
    request<{ codebase: CodebaseDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/codebases`,
      input,
    ),
  updateCodebase: (id: string, input: UpdateCodebaseInput) =>
    request<{ codebase: CodebaseDTO }>("PATCH", `/api/codebases/${encodeURIComponent(id)}`, input),
  deleteCodebase: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/codebases/${encodeURIComponent(id)}`),
  setCodebaseDependency: (id: string, input: SetCodebaseDependencyInput) =>
    request<{ ok: true }>("PUT", `/api/codebases/${encodeURIComponent(id)}/dependencies`, input),
  deleteCodebaseDependency: (id: string, toId: string) =>
    request<{ ok: true }>(
      "DELETE",
      `/api/codebases/${encodeURIComponent(id)}/dependencies/${encodeURIComponent(toId)}`,
    ),

  // environments
  environments: (projectId: string) =>
    request<{ environments: EnvironmentDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/environments`,
    ),
  createEnvironment: (projectId: string, input: CreateEnvironmentInput) =>
    request<{ environment: EnvironmentDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/environments`,
      input,
    ),
  updateEnvironment: (id: string, input: UpdateEnvironmentInput) =>
    request<{ environment: EnvironmentDTO }>(
      "PATCH",
      `/api/environments/${encodeURIComponent(id)}`,
      input,
    ),
  deleteEnvironment: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/environments/${encodeURIComponent(id)}`),

  // nasazení (Coolify/Railway) — co je kde nasazené + build logy
  deployments: (projectId: string) =>
    request<{ environments: EnvironmentDeploymentsDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/deployments`,
    ),
  deploymentLogs: (id: string) =>
    request<{ logs: string }>("GET", `/api/deployments/${encodeURIComponent(id)}/logs`),

  // Coolify servery (víc serverů — vlastní i jiných uživatelů); token se jen zapisuje
  hostingServers: () => request<{ servers: HostingServerDTO[] }>("GET", "/api/hosting-servers"),
  createHostingServer: (input: HostingServerInput) =>
    request<{ server: HostingServerDTO }>("POST", "/api/hosting-servers", input),
  updateHostingServer: (id: string, input: UpdateHostingServerInput) =>
    request<{ server: HostingServerDTO }>("PATCH", `/api/hosting-servers/${encodeURIComponent(id)}`, input),
  checkHostingServer: (id: string) =>
    request<{ ok: boolean; version: string | null; error: string | null }>(
      "POST",
      `/api/hosting-servers/${encodeURIComponent(id)}/check`,
    ),
  deleteHostingServer: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/hosting-servers/${encodeURIComponent(id)}`),
  projectHostingServer: (projectId: string) =>
    request<ProjectHostingServerDTO>("GET", `/api/projects/${encodeURIComponent(projectId)}/hosting-server`),
  setProjectHostingServer: (projectId: string, serverId: string | null) =>
    request<{ ok: true }>("PUT", `/api/projects/${encodeURIComponent(projectId)}/hosting-server`, { serverId }),

  // ---------- Fáze 3 ----------

  // organizace
  departments: () => request<{ departments: DepartmentDTO[] }>("GET", "/api/departments"),
  createDepartment: (input: UpsertDepartmentInput) =>
    request<{ department: DepartmentDTO }>("POST", "/api/departments", input),
  updateDepartment: (id: string, input: Partial<UpsertDepartmentInput>) =>
    request<{ department: DepartmentDTO }>("PATCH", `/api/departments/${encodeURIComponent(id)}`, input),
  workflows: () => request<{ workflows: WorkflowDTO[] }>("GET", "/api/workflows"),
  updateWorkflow: (key: string, input: UpdateWorkflowInput) =>
    request<{ workflow: WorkflowDTO }>("PUT", `/api/workflows/${encodeURIComponent(key)}`, input),
  projectDepartments: (projectId: string) =>
    request<{ departments: DepartmentDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/departments`,
    ),
  setProjectDepartments: (projectId: string, departmentIds: string[]) =>
    request<{ ok: true }>(
      "PUT",
      `/api/projects/${encodeURIComponent(projectId)}/departments`,
      { departmentIds },
    ),

  /** Volba motivu se ukládá na účet — vrací aktualizovanou session. */
  setTheme: (theme: ThemePreference) =>
    request<SessionInfoDTO>("PATCH", "/api/auth/me/theme", { theme }),

  /** Stav automatiky projektu — kola, brány, podmínky factory-ready. */
  projectAutomation: (projectId: string) =>
    request<AutomationStateDTO>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/automation`,
    ),

  /** Ekonomika provozu — % týdenního limitu po strojích, projektech a taskách. */
  economy: () => request<EconomyDTO>("GET", "/api/economy"),

  // tasky
  /**
   * Tasky projektu. Bez `limit` vrací celý seznam (Overview, výběr
   * závislostí); kanban si říká o jeden sloupec a jednu stránku.
   */
  tasks: (
    projectId: string,
    opts: {
      departmentId?: string;
      status?: string[];
      limit?: number;
      cursor?: string;
      /** Hledání podle názvu nebo čísla tasku (výběr závislostí). */
      q?: string;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.departmentId) q.set("departmentId", opts.departmentId);
    if (opts.status?.length) q.set("status", opts.status.join(","));
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.cursor) q.set("cursor", opts.cursor);
    if (opts.q) q.set("q", opts.q);
    const qs = q.toString();
    return request<{ tasks: TaskDTO[]; nextCursor: string | null }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/tasks${qs ? `?${qs}` : ""}`,
    );
  },
  /** Počty tasků po stavech — hlavičky sloupců kanbanu. */
  taskCounts: (projectId: string, departmentId?: string) =>
    request<{ counts: Record<string, number> }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/tasks/counts${departmentId ? `?departmentId=${encodeURIComponent(departmentId)}` : ""}`,
    ),
  task: (projectId: string, number: number) =>
    request<{ task: TaskDetailDTO }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/tasks/${number}`,
    ),
  createTask: (projectId: string, input: CreateTaskInput) =>
    request<{ task: TaskDTO }>("POST", `/api/projects/${encodeURIComponent(projectId)}/tasks`, input),
  updateTask: (id: string, input: UpdateTaskInput) =>
    request<{ task: TaskDTO }>("PATCH", `/api/tasks/${encodeURIComponent(id)}`, input),
  moveTask: (id: string, input: MoveTaskInput) =>
    request<{ task: TaskDTO; unblockedCount?: number }>(
      "POST",
      `/api/tasks/${encodeURIComponent(id)}/move`,
      input,
    ),
  pinTask: (id: string) =>
    request<{ task: TaskDTO }>("POST", `/api/tasks/${encodeURIComponent(id)}/pin`),
  unpinTask: (id: string) =>
    request<{ task: TaskDTO }>("DELETE", `/api/tasks/${encodeURIComponent(id)}/pin`),
  addTaskDependency: (id: string, input: TaskDependencyInput) =>
    request<{ task: TaskDTO }>("PUT", `/api/tasks/${encodeURIComponent(id)}/dependencies`, input),
  removeTaskDependency: (id: string, toId: string) =>
    request<{ task: TaskDTO }>(
      "DELETE",
      `/api/tasks/${encodeURIComponent(id)}/dependencies/${encodeURIComponent(toId)}`,
    ),
  addTaskComment: (id: string, text: string) =>
    request<{ ok: true }>("POST", `/api/tasks/${encodeURIComponent(id)}/comments`, { text }),
  qaVerdict: (id: string, input: QaVerdictInput) =>
    request<{ ok: true; taskStatus: string; unblockedCount: number }>(
      "POST",
      `/api/tasks/${encodeURIComponent(id)}/qa`,
      input,
    ),

  // rozhodnutí & návrhy
  decisionInbox: (projectId?: string) =>
    request<{ items: InboxItemDTO[] }>(
      "GET",
      `/api/decisions${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`,
    ),
  createDecision: (input: CreateDecisionInput) =>
    request<{ decision: DecisionDTO }>("POST", "/api/decisions", input),
  resolveDecision: (id: string, input: ResolveDecisionInput) =>
    request<{ ok: true; unblockedCount: number; approvedTaskNumber: number | null }>(
      "POST",
      `/api/decisions/${encodeURIComponent(id)}/resolve`,
      input,
    ),
  reopenDecision: (id: string) =>
    request<{ ok: true }>("POST", `/api/decisions/${encodeURIComponent(id)}/reopen`),
  proposals: (projectId: string) =>
    request<{ proposals: ProposalDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/proposals`,
    ),
  createProposal: (input: CreateProposalInput) =>
    request<{ proposal: { id: string } }>("POST", "/api/proposals", input),
  reclassifyDecision: (id: string, input: ReclassifyDecisionInput) =>
    request<{ ok: true; target: string; targetId: string }>(
      "POST",
      `/api/decisions/${encodeURIComponent(id)}/reclassify`,
      input,
    ),

  // úkoly pro člověka — vlastní entita, uzavírá se nálezem
  humanTasks: (params: { projectId?: string; status?: "OPEN" | "CLOSED" } = {}) => {
    const q = new URLSearchParams();
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.status) q.set("status", params.status);
    return request<{ items: HumanTaskDTO[] }>("GET", `/api/human-tasks?${q.toString()}`);
  },
  createHumanTask: (input: CreateHumanTaskInput) =>
    request<{ humanTask: HumanTaskDTO }>("POST", "/api/human-tasks", input),
  completeHumanTask: (id: string, input: CompleteHumanTaskInput) =>
    request<{ ok: true; humanTask: HumanTaskDTO; unblockedCount: number }>(
      "POST",
      `/api/human-tasks/${encodeURIComponent(id)}/complete`,
      input,
    ),
  dismissHumanTask: (id: string, input: DismissHumanTaskInput) =>
    request<{ ok: true; humanTask: HumanTaskDTO; unblockedCount: number }>(
      "POST",
      `/api/human-tasks/${encodeURIComponent(id)}/dismiss`,
      input,
    ),
  reopenHumanTask: (id: string) =>
    request<{ ok: true }>("POST", `/api/human-tasks/${encodeURIComponent(id)}/reopen`),

  // návrhy napříč projekty — vlastní obrazovka, bez okliky přes rozhodnutí
  proposalInbox: (params: { projectId?: string; status?: "PENDING" | "DECIDED" } = {}) => {
    const q = new URLSearchParams();
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.status) q.set("status", params.status);
    return request<{ items: ProposalDTO[] }>("GET", `/api/proposals?${q.toString()}`);
  },
  reopenProposal: (id: string) =>
    request<{ ok: true }>("POST", `/api/proposals/${encodeURIComponent(id)}/reopen`),
  decideProposal: (id: string, input: DecideProposalInput) =>
    request<{ ok: true; proposal: ProposalDTO; approvedTaskNumber: number | null }>(
      "POST",
      `/api/proposals/${encodeURIComponent(id)}/decide`,
      input,
    ),

  // blokace — čtení i odbavení člověkem (dřív uměl jen worker svým tokenem)
  blockers: (params: { projectId?: string; limit?: number; includeResolved?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.includeResolved) q.set("includeResolved", "1");
    return request<{ blockers: WorkerBlockerDTO[]; total: number; groupTotal: number }>(
      "GET",
      `/api/blockers?${q.toString()}`,
    );
  },
  workarounds: (params: { projectId?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.limit) q.set("limit", String(params.limit));
    return request<{ workarounds: WorkerWorkaroundDTO[]; total: number; groupTotal: number }>(
      "GET",
      `/api/workarounds?${q.toString()}`,
    );
  },
  blocker: (id: string) =>
    request<{ blocker: WorkerBlockerDTO; decisionId: string | null; taskStatus: string }>(
      "GET",
      `/api/blockers/${encodeURIComponent(id)}`,
    ),
  resolveBlocker: (id: string, input: WorkerBlockerResolveInput) =>
    request<{ ok: true; resolvedCount: number; lessonSlug: string | null }>(
      "POST",
      `/api/blockers/${encodeURIComponent(id)}/resolve`,
      input,
    ),

  // goals / milestones / releases
  goals: () => request<{ goals: GoalDTO[] }>("GET", "/api/goals"),
  createGoal: (input: UpsertGoalInput) => request<{ goal: GoalDTO }>("POST", "/api/goals", input),
  updateGoal: (id: string, input: Partial<UpsertGoalInput>) =>
    request<{ goal: GoalDTO }>("PATCH", `/api/goals/${encodeURIComponent(id)}`, input),
  createInitiative: (goalId: string, input: CreateInitiativeInput) =>
    request<{ initiative: { id: string } }>(
      "POST",
      `/api/goals/${encodeURIComponent(goalId)}/initiatives`,
      input,
    ),
  milestones: (projectId: string) =>
    request<{ milestones: MilestoneDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/milestones`,
    ),
  createMilestone: (projectId: string, input: UpsertMilestoneInput) =>
    request<{ milestone: MilestoneDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/milestones`,
      input,
    ),
  updateMilestone: (id: string, input: Partial<UpsertMilestoneInput>) =>
    request<{ milestone: MilestoneDTO }>("PATCH", `/api/milestones/${encodeURIComponent(id)}`, input),
  releases: (projectId: string) =>
    request<{ releases: ReleaseDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/releases`,
    ),
  createRelease: (projectId: string, input: UpsertReleaseInput) =>
    request<{ release: ReleaseDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/releases`,
      input,
    ),
  updateRelease: (id: string, input: Partial<UpsertReleaseInput>) =>
    request<{ release: ReleaseDTO }>("PATCH", `/api/releases/${encodeURIComponent(id)}`, input),

  // factory plan
  plan: () => request<FactoryPlanDTO>("GET", "/api/plan"),

  // ---------- Fáze 4 ----------

  workers: () => request<{ workers: WorkerDTO[] }>("GET", "/api/workers"),
  createWorker: (input: CreateWorkerInput) =>
    request<{ worker: WorkerDTO; token: string }>("POST", "/api/workers", input),
  updateWorker: (id: string, input: UpdateWorkerInput) =>
    request<{ worker: WorkerDTO }>("PATCH", `/api/workers/${encodeURIComponent(id)}`, input),
  reissueWorkerToken: (id: string) =>
    request<{ token: string }>("POST", `/api/workers/${encodeURIComponent(id)}/token`),
  workersActivity: (days = 7) =>
    request<WorkersActivityDTO>("GET", `/api/workers/activity?days=${days}`),
  sessions: (params: { projectId?: string; taskId?: string; status?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.taskId) q.set("taskId", params.taskId);
    if (params.status) q.set("status", params.status);
    if (params.limit) q.set("limit", String(params.limit));
    return request<{ sessions: WorkSessionSummaryDTO[] }>("GET", `/api/sessions?${q.toString()}`);
  },
  session: (id: string) =>
    request<{ session: WorkSessionDetailDTO }>("GET", `/api/sessions/${encodeURIComponent(id)}`),
  modelAnalytics: () =>
    request<{ models: ModelAnalyticsRowDTO[] }>("GET", "/api/sessions/analytics/models"),
  workerBlockers: (params: { projectId?: string; limit?: number; includeResolved?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.projectId) q.set("projectId", params.projectId);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.includeResolved) q.set("includeResolved", "1");
    return request<{ blockers: WorkerBlockerDTO[]; total: number; groupTotal: number }>(
      "GET",
      `/api/workers/blockers?${q.toString()}`,
    );
  },

  // ---------- Fáze 6 ----------

  routines: () => request<{ routines: RoutineDTO[] }>("GET", "/api/routines"),
  routine: (id: string) =>
    request<{ routine: RoutineDTO; runs: RoutineRunDTO[] }>(
      "GET",
      `/api/routines/${encodeURIComponent(id)}`,
    ),
  createRoutine: (input: CreateRoutineInput) =>
    request<{ routine: RoutineDTO }>("POST", "/api/routines", input),
  updateRoutine: (id: string, input: UpdateRoutineInput) =>
    request<{ routine: RoutineDTO }>("PATCH", `/api/routines/${encodeURIComponent(id)}`, input),
  deleteRoutine: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/routines/${encodeURIComponent(id)}`),
  runRoutineNow: (id: string) =>
    request<{ routine: RoutineDTO }>("POST", `/api/routines/${encodeURIComponent(id)}/run-now`),

  // ---------- Fáze 7 ----------

  connections: (projectId: string) =>
    request<{ connections: ConnectionDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/connections`,
    ),
  createConnection: (projectId: string, input: CreateConnectionInput) =>
    request<{ connection: ConnectionDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/connections`,
      input,
    ),
  updateConnection: (id: string, input: UpdateConnectionInput) =>
    request<{ connection: ConnectionDTO }>("PATCH", `/api/connections/${encodeURIComponent(id)}`, input),
  deleteConnection: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/connections/${encodeURIComponent(id)}`),
  setConnectionSecret: (id: string, input: { name: string; value: string }) =>
    request<{ connection: ConnectionDTO }>(
      "PUT",
      `/api/connections/${encodeURIComponent(id)}/secrets`,
      input,
    ),
  deleteConnectionSecret: (id: string, name: string) =>
    request<{ connection: ConnectionDTO }>(
      "DELETE",
      `/api/connections/${encodeURIComponent(id)}/secrets/${encodeURIComponent(name)}`,
    ),

  incidents: () => request<{ incidents: IncidentDTO[] }>("GET", "/api/incidents"),
  projectIncidents: (projectId: string) =>
    request<{ incidents: IncidentDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/incidents`,
    ),
  incident: (id: string) =>
    request<{ incident: IncidentDTO }>("GET", `/api/incidents/${encodeURIComponent(id)}`),
  createIncident: (projectId: string, input: CreateIncidentInput) =>
    request<{ incident: IncidentDTO; deduped: boolean }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/incidents`,
      input,
    ),
  updateIncident: (id: string, input: UpdateIncidentInput) =>
    request<{ incident: IncidentDTO }>("PATCH", `/api/incidents/${encodeURIComponent(id)}`, input),
  resolveIncident: (id: string, input: ResolveIncidentInput) =>
    request<{ incident: IncidentDTO }>(
      "POST",
      `/api/incidents/${encodeURIComponent(id)}/resolve`,
      input,
    ),

  // ---------- Pitch Perfect & feedback ----------

  screens: (projectId: string) =>
    request<{ screens: UiScreenDTO[]; summary: UiScreensSummaryDTO }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/screens`,
    ),
  /** Příloha tasku od člověka (screenshot) — multipart, contributor+. */
  addTaskAttachment: (projectSlug: string, number: number, file: File, note?: string) =>
    uploadRequest<{ attachment: TaskAttachmentDTO }>(
      `/api/projects/${encodeURIComponent(projectSlug)}/tasks/${number}/attachments`,
      file,
      note ? { note } : {},
    ),
  taskAttachmentImageUrl: (projectSlug: string, number: number, attachmentId: string) =>
    `/api/projects/${encodeURIComponent(projectSlug)}/tasks/${number}/attachments/${encodeURIComponent(attachmentId)}/image`,
  screenImageUrl: (projectId: string, screenId: string, version?: number) =>
    `/api/projects/${encodeURIComponent(projectId)}/screens/${encodeURIComponent(screenId)}/image${version ? `?version=${version}` : ""}`,
  screenShots: (projectId: string, screenId: string) =>
    request<{ shots: UiScreenshotDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/screens/${encodeURIComponent(screenId)}/shots`,
    ),
  reviewScreen: (projectId: string, screenId: string, input: ReviewScreenInput) =>
    request<{ screen: UiScreenDTO }>(
      "POST",
      `/api/projects/${encodeURIComponent(projectId)}/screens/${encodeURIComponent(screenId)}/review`,
      input,
    ),
  deleteScreen: (projectId: string, screenId: string) =>
    request<{ ok: true }>(
      "DELETE",
      `/api/projects/${encodeURIComponent(projectId)}/screens/${encodeURIComponent(screenId)}`,
    ),
  feedback: (projectId: string) =>
    request<{ feedback: ProjectFeedbackDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/feedback`,
    ),
  createFeedback: (projectId: string, text: string, file?: File) =>
    file
      ? uploadRequest<{ feedback: ProjectFeedbackDTO }>(
          `/api/projects/${encodeURIComponent(projectId)}/feedback`,
          file,
          { text },
        )
      : request<{ feedback: ProjectFeedbackDTO }>(
          "POST",
          `/api/projects/${encodeURIComponent(projectId)}/feedback`,
          { text },
        ),
  feedbackAttachmentUrl: (projectId: string, feedbackId: string) =>
    `/api/projects/${encodeURIComponent(projectId)}/feedback/${encodeURIComponent(feedbackId)}/attachment`,

  // ---------- Fáze 8 — Jarvis ----------

  jarvisStatus: () => request<JarvisStatusDTO>("GET", "/api/jarvis/status"),
  jarvisChat: (input: JarvisChatInput) =>
    request<JarvisReplyDTO>("POST", "/api/jarvis/chat", input),
  jarvisConfirm: (input: JarvisConfirmInput) =>
    request<JarvisReplyDTO>("POST", "/api/jarvis/confirm", input),
  jarvisTts: async (text: string): Promise<Blob> => {
    const res = await fetch("/api/jarvis/tts", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", "x-af-csrf": "1" },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new ApiRequestError(res.status, "TTS", "TTS selhalo");
    return res.blob();
  },
  // ---------- Fáze 9 — push ----------

  pushPublicKey: () => request<{ publicKey: string }>("GET", "/api/push/public-key"),
  pushSubscribe: (input: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ ok: true }>("POST", "/api/push/subscriptions", input),
  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true }>("DELETE", "/api/push/subscriptions", { endpoint }),

  jarvisStt: async (audio: Blob): Promise<{ text: string }> => {
    const form = new FormData();
    form.append("file", audio, "vstup.webm");
    const res = await fetch("/api/jarvis/stt", {
      method: "POST",
      credentials: "same-origin",
      headers: { "x-af-csrf": "1" },
      body: form,
    });
    if (!res.ok) throw new ApiRequestError(res.status, "STT", "Přepis selhal");
    return res.json() as Promise<{ text: string }>;
  },

  // policies
  policies: (projectId: string) =>
    request<{ policies: ProjectPolicyDTO[] }>(
      "GET",
      `/api/projects/${encodeURIComponent(projectId)}/policies`,
    ),
  updatePolicies: (projectId: string, input: UpdateProjectPoliciesInput) =>
    request<{ ok: true; changed: number }>(
      "PUT",
      `/api/projects/${encodeURIComponent(projectId)}/policies`,
      input,
    ),
};
